import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../../utils/cn';
import { ReactComponent as NavIcon } from '../../../icons/svg/nav.svg';
import { Button } from '../../../components/button';
import { ReactComponent as LoginIcon } from '../../../icons/svg/user.svg';
import { ReactComponent as SearchIcon } from '../../../icons/svg/search.svg';
import type { HeaderCartInfo } from './Header';
import {
    _headerVariants,
    type HeaderNavLinks,
    type HeaderUserData,
} from './Header';
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetTitle,
    SheetTrigger,
} from '../../../components/sheet';
import { MobileNavMenu } from './MobileNavMenu';
import { MobileUserMenu } from './user-menu/MobileUserMenu';
import { LogoAnimated } from '../../../components/logo-animated/LogoAnimated';
import { RoccoLink } from '../../../components/link/RoccoLink';
import { WarningBanner } from '../../../ui/warning-banner/components/WarningBanner';
import { UserCart } from './UserCart';

const _headerIconVariants = cva('z-fixed', {
    variants: {
        fill: {
            dark: 'fill-gray-900 text-gray-900',
            light: 'fill-white text-white',
            none: 'fill-none',
        },
        outline: {
            default: '',
            light: 'fill-none stroke-white',
            dark: 'fill-none stroke-gray-900',
        },
    },
    defaultVariants: {
        fill: 'dark',
        outline: 'default',
    },
});

interface MobileHeaderProps extends VariantProps<typeof _headerVariants> {
    /** Scrolled-solid theme: dark editorial pages go near-black with light icons. */
    solidTheme?: 'light' | 'dark';
    primaryLinks: HeaderNavLinks;
    secondaryLinks: HeaderNavLinks;
    profileLinks: HeaderNavLinks;
    userData: HeaderUserData;
    onLogout: () => void;
    onLoginClick: () => void;
    currentUrl: string;
    onUserCartClick: () => void;
    warningMessages?: string[];
    isLegacyBrokenRemMode?: boolean;
    onMobileSearchClick?: () => void;
    cartInfo: HeaderCartInfo;
    currentCountryCode?: string;
    /**
     * Small text rendered above the primary links in the drawer when set
     * (hero-overlay / B2B routes). Visual integration TBD; accepting the prop
     * now so the Header call site type-checks.
     */
    tagline?: string;
}

export const MobileHeader = (props: MobileHeaderProps) => {
    const {
        primaryLinks,
        secondaryLinks,
        profileLinks,
        color,
        userData,
        onLogout,
        currentUrl,
        mode,
        onUserCartClick,
        warningMessages,
        isLegacyBrokenRemMode,
        onMobileSearchClick,
        cartInfo,
        currentCountryCode,
        onLoginClick,
        solidTheme = 'light',
    } = props;

    const baseMenuColor = color === 'transparent' ? 'light' : 'dark';

    // The transparent hero header is `fixed`, so as soon as the page scrolls the
    // bar would otherwise sit over light page content. Switch it to a solid white
    // bar (with dark icons) shortly after scroll begins.
    const [scrolledPastHero, setScrolledPastHero] = useState(false);
    useEffect(() => {
        if (color !== 'transparent') return;
        const onScroll = () => setScrolledPastHero(window.scrollY > 100);
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, [color]);

    const showSolid = color === 'transparent' && scrolledPastHero;
    const solidIsDark = solidTheme === 'dark';
    // The solid presentation appears two ways: this component's own
    // scrolled-past-hero switch, and the app flipping `color` to 'solid'
    // past the hero. Theme both — dark editorial pages keep light icons on
    // near-black instead of the default white bar.
    const effectiveSolid = showSolid || color === 'solid';
    const menuColor = effectiveSolid
        ? solidIsDark
            ? 'light'
            : 'dark'
        : baseMenuColor;
    const fillClass = _headerIconVariants({ fill: menuColor });
    const outlineClass = _headerIconVariants({ outline: menuColor });
    const basicTheme = cn(
        'z-fixed md:hidden',
        _headerVariants({ color, mode }),
        // Site-wide top ad banner: the fixed bar sits below the banner and
        // slides up to the top as the banner scrolls away. Falls back to 0px
        // when no banner is present, so layout is unchanged without it.
        'top-[var(--ad-top-banner-offset,0px)]',
        effectiveSolid &&
            (solidIsDark
                ? 'bg-[#181818] text-white'
                : 'bg-white text-[#181818]'),
    );

    const [isNavOpen, setNavOpen] = useState(false);
    const headerRef = useRef<HTMLDivElement>(null);

    const { cartCount } = cartInfo;

    useEffect(() => {
        setNavOpen(false);
    }, [currentUrl]);

    // Publish the live fixed-header height (nav + optional warning) so default-
    // theme page content can clear it. A static 74px pad only covers the bar.
    useLayoutEffect(() => {
        const header = headerRef.current;
        if (!header) return;

        const layout =
            header.closest('#app-page-layout') ??
            document.getElementById('app-page-layout');
        if (!(layout instanceof HTMLElement)) return;

        const apply = () => {
            layout.style.setProperty(
                '--mobile-header-height',
                `${header.getBoundingClientRect().height}px`,
            );
        };

        apply();
        const resizeObserver = new ResizeObserver(apply);
        resizeObserver.observe(header);

        return () => {
            resizeObserver.disconnect();
            layout.style.removeProperty('--mobile-header-height');
        };
    }, [warningMessages]);

    return (
        <div ref={headerRef} className={basicTheme} id="mobile-header">
            {/* Match desktop: the warning pins above the nav row. It lives
                inside the fixed header strip so it moves with it — a page-flow
                banner would be overlapped by the fixed header. */}
            {!!warningMessages?.length && (
                <WarningBanner alerts={warningMessages} />
            )}
            {/* Keep height in sync with drawer max height. */}
            {/* packages/rocco/src/components/drawer/drawer-content.tsx */}
            <div className="relative flex h-19 items-center justify-between px-4 py-3">
                <Sheet open={isNavOpen} onOpenChange={setNavOpen}>
                    <SheetTrigger asChild>
                        <button
                            className="flex items-center justify-center border-none outline-hidden focus-visible:outline-hidden"
                            aria-label="Toggle Navigation Menu"
                        >
                            <NavIcon className={cn('', outlineClass)} />
                        </button>
                    </SheetTrigger>
                    <SheetContent
                        side="left"
                        radius="none"
                        hideDefaultCloseButton
                        noOverlay
                        zLevel="top"
                    >
                        <SheetTitle>
                            <p className="sr-only">Navigation Menu</p>
                        </SheetTitle>
                        <SheetDescription hidden>
                            <span className="sr-only">
                                Navigation Menu on the Header
                            </span>
                        </SheetDescription>

                        <MobileNavMenu
                            primaryLinks={primaryLinks}
                            secondaryLinks={secondaryLinks}
                            isAuthorized={userData.isAuthorized}
                            close={() => setNavOpen(false)}
                            currentUrl={currentUrl}
                            logout={onLogout}
                        />
                    </SheetContent>
                </Sheet>

                <RoccoLink
                    to="/"
                    className={cn(
                        'absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
                        fillClass,
                    )}
                    aria-label="Home"
                >
                    <LogoAnimated
                        size={1.6}
                        mode="disabled"
                        color={menuColor}
                        isLegacyBrokenRemMode={isLegacyBrokenRemMode}
                        disableColorTransition
                    />
                </RoccoLink>

                <div className="flex items-center gap-x-3">
                    <div className="flex gap-x-3">
                        {onMobileSearchClick ? (
                            <Button
                                size="square24"
                                shape="square"
                                color="transparent"
                                overflow="visible"
                                asChild
                                onClick={onMobileSearchClick}
                            >
                                <SearchIcon
                                    className={cn(outlineClass, 'size-6')}
                                />
                            </Button>
                        ) : (
                            <Button
                                size="square24"
                                shape="square"
                                color="transparent"
                                overflow="visible"
                                asChild
                            >
                                <RoccoLink to="/search">
                                    <SearchIcon
                                        className={cn(outlineClass, 'size-6')}
                                    />
                                </RoccoLink>
                            </Button>
                        )}
                    </div>

                    {cartCount > 0 && (
                        <UserCart
                            onUserCartClick={onUserCartClick}
                            fill={menuColor}
                            cartInfo={cartInfo}
                        />
                    )}
                    {userData.isAuthorized ? (
                        <div className="flex">
                            <MobileUserMenu
                                userData={userData}
                                profileLinks={profileLinks}
                                onLogout={onLogout}
                                currentUrl={currentUrl}
                                currentCountryCode={currentCountryCode}
                            />
                        </div>
                    ) : (
                        <div className="flex gap-x-3">
                            <Button
                                size="square24"
                                shape="square"
                                color="transparent"
                                overflow="visible"
                                asChild
                            >
                                <button
                                    aria-label="Log in"
                                    onClick={onLoginClick}
                                >
                                    <LoginIcon
                                        className={cn(outlineClass, 'size-6')}
                                    />
                                </button>
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
